import { type FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  CROSS_SELL_PDP_CENTER_SLIDER_GGLP_FIRST_IMPRESSION,
  CrossSellHorizontalSlider,
  CrossSellHorizontalSliderCompactItem,
  CrossSellWidgetContainer,
  CrossSellWidgetMoreLink,
  CrossSellWidgetProvider,
  CrossSellWidgetTitle,
  useCrossSellConfigContext,
  useCrossSellWidgetTitle,
  useSwrCrossSellHorizontalSlider,
} from '../../../../cross-sell'
import { useGlobalConfigContext } from '../../../../global-config'
import { gtmDataLayerPushCswProduct } from '../../../../gtm'
import { urlResolverGetSearchPage } from '../../../../url-handling'
import { productAccessorStockStatusIsAvailable } from '../../../model/productAccessor/productAccessorStockStatusIsAvailable'
import { useProductPageContext } from '../../productPageContext'

import { useProductCrossSellFirstImpressionWidget } from './useProductCrossSellFirstImpressionWidget'

export const ProductCrossSellFirstImpressionSliderWidget: FC = () => {
  const { widgets } = useCrossSellConfigContext()
  const { product } = useProductPageContext()
  const globalConfig = useGlobalConfigContext()

  const available = productAccessorStockStatusIsAvailable(product)
  const firstImpressionCrossSellWidget =
    useProductCrossSellFirstImpressionWidget()

  const [intersect, setIntersect] = useState(false)

  const onIntersect = useCallback(() => {
    setIntersect(true)
  }, [])

  const widgetId =
    widgets[CROSS_SELL_PDP_CENTER_SLIDER_GGLP_FIRST_IMPRESSION]?.id

  const { data, error } = useSwrCrossSellHorizontalSlider(
    intersect && widgetId && firstImpressionCrossSellWidget
      ? {
          productId: product.upid,
          visibleItemCount: 6,
          widgetId,
        }
      : undefined,
  )

  const widgetTitle = useCrossSellWidgetTitle({
    data,
    widgetType: CROSS_SELL_PDP_CENTER_SLIDER_GGLP_FIRST_IMPRESSION,
  })

  const moreHref = useMemo(
    () =>
      data?.items
        ? urlResolverGetSearchPage(globalConfig, {
            queryParams: {
              // eslint-disable-next-line id-length -- search parameter
              q: data.items.map(({ id }) => id).join(','),
            },
          })
        : undefined,
    [data?.items, globalConfig],
  )

  if (
    !widgetId ||
    /*
     * Similar products will be displayed on top when there are no variants available in stock
     * https://jira.shop-apotheke.com/browse/WSHOME-734
     */
    !available ||
    // https://jira.shop-apotheke.com/browse/CRO-576
    !firstImpressionCrossSellWidget ||
    data?.items.length === 0 ||
    error
  ) {
    return null
  }

  return (
    <CrossSellWidgetContainer
      className="mb-7 hidden tablet:block"
      onIntersect={onIntersect}
      widgetData={data}
      widgetId={widgetId}
      widgetType={CROSS_SELL_PDP_CENTER_SLIDER_GGLP_FIRST_IMPRESSION}
    >
      <CrossSellWidgetTitle className="text-m font-bold">
        {widgetTitle}
      </CrossSellWidgetTitle>
      <CrossSellWidgetMoreLink href={moreHref}>
        <FormattedMessage id="general.label.ShowAll" />
      </CrossSellWidgetMoreLink>
      <CrossSellWidgetProvider value={{ widgetId }}>
        <CrossSellHorizontalSlider
          className="[--name-line-clamp:1]"
          onGtmDataLayerPush={(cswProductId) => {
            gtmDataLayerPushCswProduct({
              cswProductId,
              productId: product.upid,
              widgetId,
              widgetTitle,
            })
          }}
          products={data?.items}
          productsPerPage={6}
          slots={{ itemContent: CrossSellHorizontalSliderCompactItem }}
        />
      </CrossSellWidgetProvider>
    </CrossSellWidgetContainer>
  )
}
